import { z } from 'zod'

// Time constants for validation
// Using Unix epoch as minimum and far future dates as maximum
const MIN_UNIX_EPOCH_MS = 0 // January 1, 1970 00:00:00 UTC
const MIN_UNIX_EPOCH_S = 0
const MAX_JS_DATE_MS = 8640000000000000 // JavaScript's maximum date value (year ~275760)
const MAX_JS_DATE_S = 8640000000000

// Threshold to distinguish between seconds and milliseconds
// Any timestamp larger than this is likely milliseconds, smaller is likely seconds
export const SECONDS_MS_THRESHOLD = 100000000000 // ~Year 5138 in seconds, ~1973 in milliseconds

export const DateRangeNumberSchema = z.object({
  startTime: z.number().positive().meta({ description: 'Start of the range, in UTC epoch milliseconds' }),
  endTime: z.number().positive().meta({ description: 'End of the range, in UTC epoch milliseconds' }),
})
export type DateRangeNumber = z.infer<typeof DateRangeNumberSchema>

export const DATE_INPUT_DESCRIPTION =
  'UTC epoch milliseconds (a number below 100000000000 is read as epoch seconds), or an ISO 8601 string. A string ending in `Z` or a `±HH:MM`, `±HHMM` or `±HH` offset after its time, such as `2026-01-15T10:00:00+0700`, is that instant; one without a time zone, such as `2026-01-15`, is read as America/Los_Angeles time.'

export const DateInputSchema = z.union([z.string(), z.number()]).meta({ description: DATE_INPUT_DESCRIPTION })

/** A range filter on a date, compared as stored, so a value in seconds would match a time in 1970. */
export const DateRangeFilterSchema = z.object({
  gte: z.number().optional().meta({ description: 'Earliest time, inclusive, in UTC epoch milliseconds' }),
  lte: z.number().optional().meta({ description: 'Latest time, inclusive, in UTC epoch milliseconds' }),
})

export const DateRangeSchema = z.object({
  startTime: DateInputSchema,
  endTime: DateInputSchema,
})
export type DateRange = z.infer<typeof DateRangeSchema>

export type DateInput = z.infer<typeof DateInputSchema>

export const UTCMillisecondsSchema = z
  .number()
  .transform((value) => Math.round(value))
  .pipe(
    z
      .number()
      .int()
      .min(MIN_UNIX_EPOCH_MS)
      .max(MAX_JS_DATE_MS)
      .refine((ms) => !isNaN(new Date(ms).getTime()), {
        message: 'Timestamps must be UTC milliseconds.',
      })
      .refine((ms) => ms >= SECONDS_MS_THRESHOLD, {
        message: 'Value appears to be in seconds, not milliseconds. Use UTCSecondsSchema instead.',
      })
  )
  .meta({
    id: 'utcMilliseconds',
    title: 'Timestamp',
    description: 'A timestamp in UTC milliseconds',
    example: 1784224980123,
  })

export const OptionalDateRangeNumberSchema = DateRangeNumberSchema.partial()
export type OptionalDateRangeNumber = z.infer<typeof OptionalDateRangeNumberSchema>

export const OptionalDateRangeSchema = DateRangeSchema.partial()

export type OptionalDateRange = z.infer<typeof OptionalDateRangeSchema>

export const UTCSecondsSchema = z
  .number()
  .transform((value) => Math.round(value))
  .pipe(
    z
      .number()
      .int()
      .min(MIN_UNIX_EPOCH_S)
      .max(MAX_JS_DATE_S)
      .refine((seconds) => !isNaN(new Date(seconds * 1000).getTime()), {
        message: 'Timestamps must be UTC seconds.',
      })
      .refine((seconds) => seconds < SECONDS_MS_THRESHOLD, {
        message: 'Value appears to be in milliseconds, not seconds. Use UTCMillisecondsSchema instead.',
      })
  )
  .meta({
    id: 'utcSeconds',
    title: 'Timestamp',
    description: 'A timestamp in UTC seconds',
    example: `$Math.floor(Date.now() / 1000)`,
  })
